import { slugify } from './stringUtils';
import {
  coerceBooleanFieldValue,
  extractConfigurationPageKey,
  isNullishOrBlank,
  isRecord,
  parseConfigurationFormPages,
  parseJsonStrict,
} from './configurationNormalizationPrimitives';
import { isJsonFieldType, jsonFieldValueError } from './jsonField';

type ValidationSchema = {
  type?: unknown;
  default?: unknown;
  multiple?: unknown;
  twig_context?: string;
  items?: ValidationSchema;
  properties?: Record<string, ValidationSchema>;
};

type ValidationResult = {
  errors: string[];
  warnings: string[];
  value: unknown;
};

type ParsedConfigurationEntries = {
  bySlug: Record<string, Record<string, unknown>>;
  byIndex: Record<string, unknown>[];
  errors: string[];
};

const isScalar = (value: unknown): value is string | number | boolean => {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
};

const normalizeValueAgainstSchema = (schema: ValidationSchema | undefined, rawValue: unknown, path: string): ValidationResult => {
  const warnings: string[] = [];
  const errors: string[] = [];
  const fieldType = typeof schema?.type === 'string' ? schema.type : '';

  if (!fieldType) {
    if (typeof rawValue === 'undefined') {
      return { warnings: [], errors: [], value: null };
    }
    warnings.push(`${path} has no schema type; value is persisted as-is.`);
    return { warnings, errors, value: rawValue };
  }

  if (fieldType === 'string' || fieldType === 'text' || fieldType === 'password' || fieldType === 'hidden') {
    if (isNullishOrBlank(rawValue)) {
      return { warnings, errors, value: null };
    }
    if (Array.isArray(rawValue) || isRecord(rawValue)) {
      errors.push(`${path} must be a string-like value.`);
      return { warnings, errors, value: null };
    }
    return { warnings, errors, value: String(rawValue) };
  }

  if (fieldType === 'number') {
    if (isNullishOrBlank(rawValue)) {
      return { warnings, errors, value: null };
    }
    const nextValue = typeof rawValue === 'number' ? rawValue : typeof rawValue === 'string' ? Number(rawValue) : NaN;
    if (!Number.isFinite(nextValue)) {
      errors.push(`${path} must be a finite number.`);
      return { warnings, errors, value: null };
    }
    return { warnings, errors, value: nextValue };
  }

  if (fieldType === 'boolean' || fieldType === 'switch') {
    const fallback = typeof schema?.default === 'boolean' ? schema.default : false;
    if (isNullishOrBlank(rawValue)) {
      return { warnings, errors, value: fallback };
    }
    return { warnings, errors, value: coerceBooleanFieldValue(rawValue, fallback) };
  }

  if (
    fieldType === 'select' ||
    fieldType === 'select_via_api' ||
    fieldType === 'select_via_api_external' ||
    fieldType === 'select_via_http' ||
    fieldType === 'select_via_webhook'
  ) {
    if (isNullishOrBlank(rawValue)) {
      return { warnings, errors, value: schema?.multiple ? [] : null };
    }
    if (Array.isArray(rawValue)) {
      const invalidOption = rawValue.find((item) => !isScalar(item));
      if (invalidOption) {
        errors.push(`${path} has invalid select option values.`);
        return { warnings, errors, value: schema?.multiple ? [] : null };
      }
      return { warnings, errors, value: rawValue };
    }
    if (!isScalar(rawValue)) {
      errors.push(`${path} must be a scalar select value.`);
      return { warnings, errors, value: schema?.multiple ? [] : null };
    }
    return { warnings, errors, value: rawValue };
  }

  if (fieldType === 'autocomplete-freesolo') {
    if (isNullishOrBlank(rawValue)) {
      return { warnings, errors, value: [] };
    }
    if (!Array.isArray(rawValue)) {
      errors.push(`${path} must be a string array.`);
      return { warnings, errors, value: [] };
    }
    const invalidOption = rawValue.find((item) => typeof item !== 'string');
    if (typeof invalidOption !== 'undefined') {
      errors.push(`${path} must contain only strings.`);
      return { warnings, errors, value: [] };
    }
    return { warnings, errors, value: rawValue };
  }

  if (fieldType === 'object' || fieldType === 'schema') {
    if (isNullishOrBlank(rawValue)) {
      return { warnings, errors, value: {} };
    }
    if (!isRecord(rawValue)) {
      errors.push(`${path} must be an object.`);
      return { warnings, errors, value: {} };
    }

    const properties = isRecord(schema?.properties) ? (schema?.properties as Record<string, ValidationSchema>) : {};
    const nextObject: Record<string, unknown> = {};
    Object.keys(properties).forEach((fieldKey) => {
      const nested = normalizeValueAgainstSchema(properties[fieldKey], rawValue[fieldKey], `${path}.${fieldKey}`);
      warnings.push(...nested.warnings);
      errors.push(...nested.errors);
      nextObject[fieldKey] = nested.value;
    });

    Object.keys(rawValue).forEach((fieldKey) => {
      if (fieldKey in properties || fieldKey === 'page') {
        return;
      }
      warnings.push(`${path}.${fieldKey} is not defined in the schema and will be ignored.`);
    });

    return { warnings, errors, value: nextObject };
  }

  if (fieldType === 'array') {
    if (isNullishOrBlank(rawValue)) {
      return { warnings, errors, value: [] };
    }
    if (!Array.isArray(rawValue)) {
      errors.push(`${path} must be an array.`);
      return { warnings, errors, value: [] };
    }

    const itemSchema = schema?.items;
    const normalizedItems = rawValue.map((item, index) => {
      const nested = normalizeValueAgainstSchema(itemSchema, item, `${path}[${index}]`);
      warnings.push(...nested.warnings);
      errors.push(...nested.errors);
      return nested.value;
    });
    return { warnings, errors, value: normalizedItems };
  }

  if (isJsonFieldType(fieldType)) {
    if (isNullishOrBlank(rawValue)) {
      return { warnings, errors, value: null };
    }
    const error = jsonFieldValueError(fieldType, rawValue, path);
    if (error) {
      errors.push(error);
      return { warnings, errors, value: null };
    }
    // Keep the stored form: an untouched legacy JSON string must not read as a change.
    return { warnings, errors, value: rawValue };
  }

  if (fieldType === 'twig' || fieldType === 'ftp_connection' || fieldType === 'as2_connection') {
    if (isNullishOrBlank(rawValue)) {
      return { warnings, errors, value: null };
    }
    return { warnings, errors, value: rawValue };
  }

  warnings.push(`${path} uses unsupported field type "${fieldType}"; value is persisted as-is.`);
  return { warnings, errors, value: typeof rawValue === 'undefined' ? null : rawValue };
};

const parseConfigurationEntries = (subscriptionConfiguration: unknown): ParsedConfigurationEntries => {
  const errors: string[] = [];
  const bySlug: Record<string, Record<string, unknown>> = {};
  const byIndex: Record<string, unknown>[] = [];

  const parsedConfig = parseJsonStrict(subscriptionConfiguration, 'Subscription configuration');
  if (!parsedConfig.ok) {
    return { bySlug, byIndex, errors: [parsedConfig.error] };
  }

  if (parsedConfig.value === null || typeof parsedConfig.value === 'undefined' || parsedConfig.value === '') {
    return { bySlug, byIndex, errors };
  }

  const storePage = (rawEntry: unknown, label: string): Record<string, unknown> | null => {
    const parsedEntry = parseJsonStrict(rawEntry, label);
    if (!parsedEntry.ok) {
      errors.push(parsedEntry.error);
      return null;
    }
    if (!isRecord(parsedEntry.value)) {
      errors.push(`${label} must resolve to a JSON object.`);
      return null;
    }
    return parsedEntry.value;
  };

  if (Array.isArray(parsedConfig.value)) {
    parsedConfig.value.forEach((entry, index) => {
      const parsedEntry = storePage(entry, `Configuration page entry ${index + 1}`);
      if (!parsedEntry) {
        return;
      }
      byIndex[index] = parsedEntry;
      if (typeof parsedEntry.page === 'string' && parsedEntry.page.trim().length > 0) {
        bySlug[slugify(parsedEntry.page) || parsedEntry.page] = parsedEntry;
      }
    });
    return { bySlug, byIndex, errors };
  }

  if (isRecord(parsedConfig.value)) {
    Object.entries(parsedConfig.value).forEach(([key, entry], index) => {
      const parsedEntry = storePage(entry, `Configuration page "${key}"`);
      if (!parsedEntry) {
        return;
      }
      byIndex[index] = parsedEntry;
      const normalizedKey = slugify(key) || key;
      bySlug[normalizedKey] = parsedEntry;
      if (typeof parsedEntry.page === 'string' && parsedEntry.page.trim().length > 0) {
        bySlug[slugify(parsedEntry.page) || parsedEntry.page] = parsedEntry;
      }
    });
    return { bySlug, byIndex, errors };
  }

  errors.push('Subscription configuration must be a JSON array or object.');
  return { bySlug, byIndex, errors };
};

export const validateSubscriptionConfigurationAgainstForm = (
  configurationForm: unknown,
  subscriptionConfiguration: unknown
): { errors: string[]; warnings: string[]; normalizedConfigurationByPage: Record<string, unknown> } => {
  const parsedForm = parseConfigurationFormPages(configurationForm);
  const entryResult = parseConfigurationEntries(subscriptionConfiguration);
  const formErrors = parsedForm.parseError ? [parsedForm.parseError] : [];

  const errors = [...formErrors, ...entryResult.errors];
  const warnings: string[] = [];
  const normalizedConfigurationByPage: Record<string, unknown> = {};

  if (parsedForm.pages.length === 0 || errors.length > 0) {
    return {
      errors,
      warnings,
      normalizedConfigurationByPage,
    };
  }

  parsedForm.pages.forEach((page, pageIndex) => {
    const pageKey = extractConfigurationPageKey(page, pageIndex);
    const pageProperties = isRecord(page.properties) ? (page.properties as Record<string, ValidationSchema>) : {};
    const pageSource =
      entryResult.bySlug[pageKey] ?? (isRecord(entryResult.byIndex[pageIndex]) ? (entryResult.byIndex[pageIndex] as Record<string, unknown>) : {});

    const normalizedPage: Record<string, unknown> = { ...pageSource };
    delete normalizedPage.page;
    Object.keys(pageProperties).forEach((fieldKey) => {
      const validation = normalizeValueAgainstSchema(pageProperties[fieldKey], pageSource[fieldKey], `${pageKey}.${fieldKey}`);
      warnings.push(...validation.warnings);
      errors.push(...validation.errors);
      normalizedPage[fieldKey] = validation.value;
    });

    Object.keys(pageSource).forEach((fieldKey) => {
      if (fieldKey in pageProperties || fieldKey === 'page') {
        return;
      }
      warnings.push(`${pageKey}.${fieldKey} is not defined in the schema and will be ignored.`);
    });

    normalizedConfigurationByPage[pageKey] = normalizedPage;
  });

  return { errors, warnings, normalizedConfigurationByPage };
};
